import { Component, DestroyRef, inject, OnInit, OnDestroy, signal, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ButtonComponent, IconComponent, InputComponent } from 'nebula-ui-kit';
import {
  AuthService,
  VaultAccountBlockedService,
  VaultCompanyBootstrapService,
  VAULT_ACCOUNT_BLOCKED_SCREEN_TITLE,
  consumeVaultLoginBlockedMessage,
  resetVaultAccountBlockedCircuit,
  resolveBrowserCredentialUsername,
  VaultAlertAutoDismissController,
  type VaultAccountBlockedState,
} from '@nebula/shared-vault/data-access';
import { ERROR_LOGIN_MESSAGES } from '../../../../const/src/lib/login.constant';
import { LoginService } from '../../../../data-access/src/lib/login.service';
import { LoginInputAutocompleteDirective } from './login-input-autocomplete.directive';
import { catchError, switchMap, take, throwError, of } from 'rxjs';
@Component({
  selector: 'nebula-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    InputComponent,
    ButtonComponent,
    IconComponent,
    LoginInputAutocompleteDirective,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly accountBlockedService = inject(VaultAccountBlockedService);
  private readonly companyBootstrap = inject(VaultCompanyBootstrapService);
  private readonly loginService = inject(LoginService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  readonly resizeListener: () => void;
  readonly renderer = inject(Renderer2);
  form!: FormGroup;
  hidePassword = true;
  loading = signal(false);
  error = signal<{ TITLE: string; SUBTITLE: string } | null>(null);
  /** Marca el alert como "cerrándose" para animar la salida antes de quitarlo. */
  errorClosing = signal(false);
  private readonly errorAutoDismiss = new VaultAlertAutoDismissController({
    onBeginClose: () => this.errorClosing.set(true),
    onDismiss: () => {
      this.error.set(null);
      this.errorClosing.set(false);
    },
  });
  isDarkMode = signal<boolean>(false);
  isMobile = false;
  isTablet = false;
  constructor() {
    this.checkScreenSize();
    this.resizeListener = this.checkScreenSize.bind(this);
    this.form = this.fb.group({
      user: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
  }
  ngOnInit() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener);
    }
    const pendingBlocked = consumeVaultLoginBlockedMessage();
    if (pendingBlocked) {
      this.setError({
        TITLE: pendingBlocked.title,
        SUBTITLE: pendingBlocked.subtitle,
      });
    }
    const savedDarkMode = this.havesaveDarkMode();
    this.isDarkMode.set(savedDarkMode);
    this.applyTheme(savedDarkMode);
  }
  private havesaveDarkMode(): boolean {
    const darkMode = localStorage.getItem('dark_mode');
    return darkMode === 'true';
  }
  private applyTheme(isDark: boolean): void {
    const htmlElement = document.documentElement;
    if (isDark) {
      this.renderer.addClass(htmlElement, 'dark-theme');
    } else {
      this.renderer.removeClass(htmlElement, 'dark-theme');
    }
  }
  dismissError(): void {
    this.setError(null);
  }

  /** Muestra u oculta el alert; los mensajes se auto-cierran solos (como el de credenciales). */
  private setError(next: { TITLE: string; SUBTITLE: string } | null): void {
    this.errorAutoDismiss.cancel();
    this.errorClosing.set(false);
    this.error.set(next);
    if (next) {
      this.errorAutoDismiss.start();
    }
  }

  private showError(error: string) {
    this.setError(null);
    setTimeout(() => {
      if (this.destroyed) return;
      this.setError({
        TITLE: 'Error de autenticación',
        SUBTITLE: this.getErrorMessage(error),
      });
    });
  }

  private showAccountBlockedError(blocked: VaultAccountBlockedState): void {
    this.setError({
      TITLE: VAULT_ACCOUNT_BLOCKED_SCREEN_TITLE,
      SUBTITLE: blocked.message,
    });
  }

  private isVaultAccountBlockedState(err: unknown): err is VaultAccountBlockedState {
    if (typeof err !== 'object' || err === null) return false;
    const reason = (err as VaultAccountBlockedState).reason;
    return reason === 'inactive' || reason === 'revoked' || reason === 'unknown';
  }

  private handleAccountBlockedAtLogin(blocked: VaultAccountBlockedState): void {
    this.loading.set(false);
    this.accountBlockedService.rejectBlockedAccountAtLogin(blocked);
    this.showAccountBlockedError(blocked);
  }

  private getErrorMessage(error: string): string {
    if (error) return error;
    return '';
  }
  private checkScreenSize(): void {
    const width = window.innerWidth;
    this.isMobile = width <= 768;
    this.isTablet = width > 768 && width <= 1024;
  }
  getError(key: string) {
    const control = this.form.get(key);
    if (!control || !control.touched) return;
    if (control.errors?.['required']) return ERROR_LOGIN_MESSAGES.VALIDATION.REQUIRED;
    return '';
  }

  private tryStoreBrowserCredential(username: string, password: string): void {
    if (typeof window === 'undefined' || !username || !password) return;
    const PasswordCredentialCtor = (
      window as unknown as {
        PasswordCredential?: new (init: {
          id: string;
          password: string;
          name?: string;
        }) => Credential;
      }
    ).PasswordCredential;
    if (!PasswordCredentialCtor || !navigator.credentials?.store) return;

    try {
      const credential = new PasswordCredentialCtor({
        id: username,
        password,
        name: username,
      });
      void navigator.credentials.store(credential);
    } catch {
      // El navegador puede rechazar el almacenamiento fuera del gesto del usuario.
    }
  }

  private isVaultUser(): boolean {
    const currentUser = this.auth.getCurrentUser();
    if (!currentUser) return false;

    return (
      currentUser.email?.includes('@datavault.com') ||
      (currentUser as any).nit?.startsWith('DATAVAULT') ||
      currentUser.customerId === 8 ||
      (currentUser as any).customer?.id === 8
    );
  }

  onSubmit(): void {
    this.setError(null);

    if (this.form.valid) {
      this.loading.set(true);

      const { user, password } = this.form.value;
      const credentials = {
        user: user.trim(),
        password: password.trim(),
      };

      this.loginService
        .login(credentials)
        .pipe(
          switchMap(() =>
            this.auth.validateTokenAndGetSession().pipe(
              catchError(() =>
                this.auth.isAuthenticated() && this.auth.getMenuRoleIds().length > 0
                  ? of(undefined)
                  : throwError(() => 'session-validation-failed'),
              ),
            ),
          ),
          switchMap(() => this.accountBlockedService.checkAccountAccess$()),
          switchMap(() => this.companyBootstrap.tryAutoEnterAfterLogin()),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (result) => {
            this.tryStoreBrowserCredential(
              resolveBrowserCredentialUsername(credentials.user),
              credentials.password,
            );
            this.loading.set(false);
            resetVaultAccountBlockedCircuit();
            this.auth.resumeVaultCookieSyncAfterLogin();
            void this.router.navigate([result.route], {
              ...(result.autoSelected && result.companyId
                ? {
                    queryParams: {
                      companyId: result.companyId,
                      autoRedirect: 'true',
                    },
                  }
                : {}),
            });
          },
          error: (err: unknown) => {
            if (this.isVaultAccountBlockedState(err)) {
              this.handleAccountBlockedAtLogin(err);
              return;
            }
            if (err === 'session-validation-failed') {
              this.loading.set(false);
              this.showError(
                'No se pudo validar la sesión. Cierra sesión, vuelve a entrar o revisa los roles del usuario.',
              );
              return;
            }
            this.loading.set(false);
            this.showError(
              typeof err === 'string' ? err : 'Error en la autenticación',
            );
          },
        });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
  toggleDarkMode(): void {
    const newDarkMode = !this.isDarkMode();
    this.isDarkMode.set(newDarkMode);
    localStorage.setItem('dark_mode', newDarkMode.toString());
    this.applyTheme(newDarkMode);
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.errorAutoDismiss.cancel();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
